function nffEquivalentSize(department, value) {
  const match = /^(US|EU)\s+(\d+(?:\.\d+)?)$/.exec(value);
  if (!match) return null;
  const chart = department === 'women'
    ? {'5':36,'6':37,'7':38,'8':39,'9':40,'10':41,'11':42,'12':43}
    : department === 'men'
      ? {'7':40,'8':41,'9':42,'10':43,'11':44,'12':45,'13':46}
      : {};
  if (match[1] === 'US') return chart[match[2]] ? 'EU ' + chart[match[2]] : null;
  const us = Object.keys(chart).find(size => String(chart[size]) === match[2]);
  return us ? 'US ' + us : null;
}

function nffSizeChoices(department, values) {
  const choices = new Map();
  for (const value of values) {
    if (!/^(US|EU)\s+\d+(?:\.\d+)?$/.test(value.label)) continue;
    choices.set(value.label, {label:value.label, value:value.value ?? value.label});
  }
  for (const label of [...choices.keys()]) {
    const equivalent = nffEquivalentSize(department, label);
    if (equivalent && !choices.has(equivalent)) choices.set(equivalent, {label:equivalent, value:equivalent});
  }
  return [...choices.values()].sort((a,b) => Number(a.label.split(' ')[1]) - Number(b.label.split(' ')[1]));
}

if (!customElements.get('nff-size-finder')) customElements.define('nff-size-finder', class extends HTMLElement {
  async connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.form = this.querySelector('form');
    this.department = this.querySelector('[data-department]');
    this.system = this.querySelector('[data-system]');
    this.size = this.querySelector('[data-size]');
    try { this.data = JSON.parse(this.querySelector('script[type="application/json"]').textContent); } catch { return; }
    this.department.addEventListener('change', () => this.refresh(false), {signal});
    this.system.addEventListener('change', () => this.refresh(true), {signal});
    this.form.addEventListener('submit', event => {
      this.form.querySelectorAll('[data-nff-equivalent]').forEach(input => input.remove());
      if (!this.size.value) { event.preventDefault(); this.size.focus(); return; }
      const equivalent = nffEquivalentSize(this.department.value, this.size.value);
      if (equivalent) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = this.size.name;
        input.value = equivalent;
        input.dataset.nffEquivalent = '';
        this.form.append(input);
      }
    }, {signal});
    this.refresh(false);
    for (const key of ['women','men']) {
      if (!this.data[key]?.values.length) {
        try {
          const endpoint = new URL(this.data[key].url, location.origin);
          endpoint.searchParams.set('section_id','nff-size-data');
          const response = await fetch(endpoint, {signal, credentials:'same-origin'});
          if (!response.ok) continue;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          this.data[key] = JSON.parse(html.querySelector('[data-nff-size-data]').textContent);
        } catch (error) { if (error.name === 'AbortError') return; }
      }
    }
    this.refresh(true);
  }
  refresh(keepSystem) {
    const department = this.department.value;
    const data = this.data[department];
    if (!data) return;
    const params = new URL(location.href).searchParams;
    const active = params.getAll(data.param).flatMap(value => value.split(','));
    const previous = this.loadedDepartment === department ? this.size.value : '';
    const wanted = keepSystem ? this.system.value : active[0]?.split(' ')[0];
    const supported = nffSizeChoices(department, data.values);
    const systems = ['US','EU'].filter(system => supported.some(value => value.label.startsWith(system + ' ')));
    this.system.replaceChildren(...systems.map(value => new Option(value,value)));
    if (systems.includes(wanted)) this.system.value = wanted;
    const values = supported.filter(value => value.label.startsWith(this.system.value + ' '));
    this.size.replaceChildren(new Option(this.dataset.placeholder,''), ...values.map(value => new Option(value.label,value.value)));
    const selected = [previous, ...active].find(value => value && values.some(choice => choice.value === value));
    if (selected) this.size.value = selected;
    this.size.name = data.param;
    this.form.action = data.url;
    this.system.disabled = !systems.length;
    this.size.disabled = !values.length;
    this.form.querySelector('button').disabled = !values.length;
    this.loadedDepartment = department;
  }
  disconnectedCallback() { this.abort?.abort(); }
});
document.addEventListener('keydown',e=>{if(e.key==='Escape'){document.querySelectorAll('.nff-header details[open]').forEach(d=>{d.open=false;d.querySelector('summary')?.focus()})}});
document.addEventListener('click',e=>{document.querySelectorAll('.nff-desktop-nav details[open]').forEach(d=>{if(!d.contains(e.target))d.open=false})});

if (!customElements.get('nff-stock-results')) customElements.define('nff-stock-results', class extends HTMLElement {
  connectedCallback() {
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this, {childList:true,subtree:true});
    this.schedule();
  }
  disconnectedCallback() { this.observer?.disconnect(); this.abort?.abort(); clearTimeout(this.timer); }
  schedule() { clearTimeout(this.timer); this.timer=setTimeout(()=>this.sync(),80); }
  async sync() {
    const meta=this.querySelector('[data-nff-stock-meta]');
    if (!meta || meta.dataset.active!=='true') { this.abort?.abort(); return; }
    const results=this.querySelector('#ResultsList');
    let grid=results?.querySelector('.product-grid');
    if (!results) return;
    const key=location.pathname+location.search;
    if (grid?.dataset.nffStockReady===key || (this.loadingGrid===grid && this.loadingKey===key)) return;
    this.loadingGrid=grid; this.loadingKey=key;
    this.abort?.abort(); this.abort=new AbortController();
    const controller=this.abort; const signal=controller.signal;
    const counts=this.querySelectorAll('[data-testid="products-count"] [role="status"]');
    counts.forEach(e=>e.textContent=meta.dataset.loading);
    const current=Number(meta.dataset.page), pages=Number(meta.dataset.pages);
    const items=new Map();
    try {
      const pending=Array.from({length:pages},(_,i)=>i+1);
      for (let offset=0;offset<pending.length;offset+=3) {
        await Promise.all(pending.slice(offset,offset+3).map(async page=>{
          const url=new URL(location.href);url.searchParams.set('section_id',meta.dataset.section);url.searchParams.set('page',String(page));
          const response=await fetch(url,{signal,credentials:'same-origin'});
          if (!response.ok) throw new Error('Stock results unavailable');
          const document=new DOMParser().parseFromString(await response.text(),'text/html');
          if (!document.querySelector('[data-nff-stock-meta]')) throw new Error('Stock results unavailable');
          items.set(page,[...document.querySelectorAll('#ResultsList .product-grid > li')]);
        }));
      }
      if (signal.aborted || !meta.isConnected || key!==location.pathname+location.search) return;
      const cards=[...items.entries()].sort((a,b)=>a[0]-b[0]).flatMap(([,cards])=>cards);
      if (!grid) {
        grid=document.createElement('ul');grid.className='product-grid';grid.setAttribute('role','list');results.append(grid);
      }
      grid.dataset.nffStockReady=key; grid.dataset.lastPage='1';
      cards.forEach((card,index)=>{card.dataset.page='1';card.hidden=index>=24;});
      grid.replaceChildren(...cards);
      results.querySelector('.pagination')?.remove();
      results.querySelector('.main-collection-grid__empty')?.remove();
      results.querySelector('.nff-stock-more')?.remove();
      results.querySelector('.nff-stock-empty')?.remove();
      counts.forEach(e=>e.textContent=meta.dataset.count.replace('__COUNT__',String(cards.length)));
      if (!cards.length) {const empty=document.createElement('p');empty.className='nff-stock-empty';empty.textContent=meta.dataset.empty;results.append(empty);}
      if (cards.length>24) {
        const more=document.createElement('button');more.type='button';more.className='nff-btn nff-stock-more';more.textContent=meta.dataset.more;
        let visible=24;more.addEventListener('click',()=>{cards.slice(visible,visible+24).forEach(card=>card.hidden=false);visible+=24;if(visible>=cards.length)more.remove();});results.append(more);
      }
    } catch(error) {
      if(error.name!=='AbortError' && meta.isConnected) { if(grid)grid.dataset.nffStockReady=key; counts.forEach(e=>e.textContent=meta.dataset.partial); }
    } finally { if(this.abort===controller){this.loadingGrid=null;this.loadingKey=null;} }
  }
});
